import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { recibosApi, cajaApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSucursal } from '../context/SucursalContext';
import { formatDate } from '../utils/dateHelpers';
import PagoMixtoForm, { lineaVacia, validarPagoMixto } from '../components/PagoMixtoForm';

const Recibos = () => {
  const { user } = useAuth();
  const { sucursalActual, getSucursalId } = useSucursal();

  // Estado del listado
  const [recibos, setRecibos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [busqueda, setBusqueda] = useState('');
  const [stats, setStats] = useState(null);

  // Estado del formulario
  const [showForm, setShowForm] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [form, setForm] = useState({
    fecha: new Date().toISOString().split('T')[0],
    recibido_de: '',
    direccion: '',
    nit: 'C/F',
    items: [{ cantidad: 1, descripcion: '', precio_unitario: 0, total: 0 }],
    total_letras: '',
    metodo_pago: 'Efectivo',
    cuenta_bancaria_id: '',
    pos_id: '',
    comprobante: '',
    pagos_mixtos: [lineaVacia()]
  });

  const [cuentas, setCuentas] = useState([]);
  const [posTerminales, setPosTerminales] = useState([]);

  useEffect(() => {
    const tiendaId = getSucursalId();
    if (!tiendaId) return;
    Promise.all([cajaApi.getCuentas(tiendaId), cajaApi.getPos(tiendaId)])
      .then(([resCuentas, resPos]) => {
        setCuentas(resCuentas || []);
        setPosTerminales(resPos || []);
      })
      .catch(err => console.error('Error cargando cuentas/POS:', err));
  }, [getSucursalId]);

  const loadRecibos = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      const tiendaId = getSucursalId();
      if (tiendaId) params.tienda_id = tiendaId;

      const data = await recibosApi.getAll(params);
      setRecibos(data.data || []);
      setPagination(data.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      console.error('Error al cargar recibos:', err);
    } finally {
      setLoading(false);
    }
  }, [getSucursalId]);

  const loadStats = useCallback(async () => {
    try {
      const params = {};
      const tiendaId = getSucursalId();
      if (tiendaId) params.tienda_id = tiendaId;
      const data = await recibosApi.getStats(params);
      setStats(data);
    } catch (err) {
      console.error('Error al cargar estadísticas:', err);
    }
  }, [getSucursalId]);

  useEffect(() => {
    loadRecibos();
    loadStats();
  }, [loadRecibos, loadStats]);

  // Busqueda
  const handleBuscar = async () => {
    if (!busqueda.trim()) {
      loadRecibos();
      return;
    }
    setLoading(true);
    try {
      const data = await recibosApi.search(busqueda);
      setRecibos(data || []);
      setPagination({ page: 1, pages: 1, total: (data || []).length });
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Funciones de numero a letras
  const numeroALetras = (num) => {
    const unidades = ['', 'uno', 'dos', 'tres', 'cuatro', 'cinco', 'seis', 'siete', 'ocho', 'nueve'];
    const decenas = ['', 'diez', 'veinte', 'treinta', 'cuarenta', 'cincuenta', 'sesenta', 'setenta', 'ochenta', 'noventa'];
    const especiales = ['diez', 'once', 'doce', 'trece', 'catorce', 'quince', 'dieciseis', 'diecisiete', 'dieciocho', 'diecinueve'];
    const centenas = ['', 'ciento', 'doscientos', 'trescientos', 'cuatrocientos', 'quinientos', 'seiscientos', 'setecientos', 'ochocientos', 'novecientos'];
    if (num === 0) return 'cero';
    if (num === 100) return 'cien';
    let resultado = '';
    if (num >= 1000) {
      const miles = Math.floor(num / 1000);
      resultado += (miles === 1 ? 'mil ' : convertirMenorMil(miles, unidades, decenas, especiales, centenas) + ' mil ');
      num %= 1000;
    }
    resultado += convertirMenorMil(num, unidades, decenas, especiales, centenas);
    return resultado.trim();
  };

  const convertirMenorMil = (num, unidades, decenas, especiales, centenas) => {
    let resultado = '';
    if (num >= 100) {
      if (num === 100) return 'cien';
      resultado += centenas[Math.floor(num / 100)] + ' ';
      num %= 100;
    }
    if (num >= 10 && num <= 19) {
      resultado += especiales[num - 10];
    } else if (num >= 20) {
      const dec = Math.floor(num / 10);
      const uni = num % 10;
      resultado += uni === 0 ? decenas[dec] : decenas[dec] + ' y ' + unidades[uni];
    } else if (num > 0) {
      resultado += unidades[num];
    }
    return resultado.trim();
  };

  const calcularTotal = (items) => items.reduce((sum, item) => sum + (parseFloat(item.total) || 0), 0);

  const actualizarTotalLetras = (items) => {
    const totalGeneral = calcularTotal(items);
    const parteEntera = Math.floor(totalGeneral);
    const centavos = Math.round((totalGeneral - parteEntera) * 100);
    let totalLetras = numeroALetras(parteEntera);
    totalLetras += centavos > 0 ? ` con ${centavos}/100` : ' exactos';
    return totalLetras.charAt(0).toUpperCase() + totalLetras.slice(1);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index][field] = value;
    if (field === 'cantidad' || field === 'precio_unitario') {
      newItems[index].total = (parseFloat(newItems[index].cantidad) || 0) * (parseFloat(newItems[index].precio_unitario) || 0);
    }
    setForm(prev => ({ ...prev, items: newItems, total_letras: actualizarTotalLetras(newItems) }));
  };

  const agregarItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { cantidad: 1, descripcion: '', precio_unitario: 0, total: 0 }]
    }));
  };

  const eliminarItem = (index) => {
    if (form.items.length > 1) {
      const newItems = form.items.filter((_, i) => i !== index);
      setForm(prev => ({ ...prev, items: newItems, total_letras: actualizarTotalLetras(newItems) }));
    }
  };

  const formatMoney = (amount) => `Q${parseFloat(amount || 0).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`;

  const formatDateLong = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                   'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    return `${date.getDate()} de ${meses[date.getMonth()]} de ${date.getFullYear()}`;
  };

  // Guardar recibo en la base de datos
  const handleGuardar = async () => {
    if (!form.recibido_de.trim()) {
      alert('Debe ingresar el nombre de quien se recibe el pago');
      return;
    }
    const itemsValidos = form.items.filter(item => item.descripcion.trim() && parseFloat(item.total) > 0);
    if (itemsValidos.length === 0) {
      alert('Debe agregar al menos un item con descripcion y precio');
      return;
    }
    if (form.metodo_pago === 'Transferencia' && !form.cuenta_bancaria_id) {
      alert('Debe seleccionar la cuenta bancaria');
      return;
    }
    if (form.metodo_pago === 'POS' && !form.pos_id) {
      alert('Debe seleccionar la terminal POS');
      return;
    }
    if (form.metodo_pago === 'Mixto') {
      const totalItems = calcularTotal(itemsValidos);
      const errorMixto = validarPagoMixto(form.pagos_mixtos, totalItems);
      if (errorMixto) {
        alert(errorMixto);
        return;
      }
    }

    setGuardando(true);
    try {
      const tiendaId = getSucursalId();
      const result = await recibosApi.create({
        fecha: form.fecha,
        recibido_de: form.recibido_de,
        direccion: form.direccion,
        nit: form.nit,
        total_letras: form.total_letras,
        items: itemsValidos,
        metodo_pago: form.metodo_pago,
        cuenta_bancaria_id: form.cuenta_bancaria_id || null,
        pos_id: form.pos_id || null,
        pagos_mixtos: form.metodo_pago === 'Mixto' ? form.pagos_mixtos : undefined,
        creado_por: user?.id,
        tienda_id: tiendaId || null
      });

      // Imprimir el recibo despues de guardar
      imprimirRecibo(result.numero_recibo, form, itemsValidos);

      // Limpiar y recargar
      limpiarFormulario();
      setShowForm(false);
      loadRecibos();
      loadStats();
      alert(`Recibo N° ${result.numero_recibo} guardado exitosamente`);
    } catch (err) {
      console.error('Error:', err);
      alert('Error al guardar el recibo: ' + (err.response?.data?.error || err.message));
    } finally {
      setGuardando(false);
    }
  };

  const imprimirRecibo = (numero, datos, items) => {
    const printWindow = window.open('about:blank', '_blank', 'width=400,height=600');
    if (!printWindow) {
      alert('Por favor habilite las ventanas emergentes para imprimir');
      return;
    }

    const tiendaNombre = sucursalActual?.nombre || 'INTERSECOM';
    const tiendaDireccion = sucursalActual?.direccion || '15 Av. 1-340 Zona 5, San Marcos';
    const tiendaTelefono = sucursalActual?.telefono || '7760-3991 / 7725-4830';
    const total = calcularTotal(items);

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <title>Recibo de Caja N° ${numero}</title>
          <style>
            @media print {
              @page { size: 72mm auto; margin: 0 !important; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              body {
                font-family: Arial, Helvetica, sans-serif !important;
                font-size: 11px !important; font-weight: 600 !important; line-height: 1.3 !important;
                margin: 0 !important; padding: 0 !important; width: 72mm !important;
                color: #000 !important; background: #fff !important;
                text-shadow: 0.3px 0 0 #000, -0.3px 0 0 #000 !important;
              }
              .rc { width: 72mm !important; padding: 2mm !important; box-sizing: border-box !important; }
              .rc-header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 3mm; margin-bottom: 3mm; }
              .rc-logo { font-size: 16px !important; font-weight: 900 !important; letter-spacing: 1px; }
              .rc-sub { font-size: 10px !important; font-weight: 700 !important; margin-bottom: 2px; }
              .rc-tipo { text-align: center; font-size: 13px !important; font-weight: 900 !important; margin: 3mm 0; padding: 2mm; border: 2px solid #000; }
              .rc-num { text-align: center; font-size: 12px !important; font-weight: 800 !important; margin-bottom: 3mm; }
              .rc-row { display: flex; justify-content: space-between; font-size: 10px !important; font-weight: 600 !important; margin-bottom: 1mm; }
              .rc-label { font-weight: 800 !important; }
              .rc-table { width: 100%; border-collapse: collapse; margin: 3mm 0; }
              .rc-table th { border-bottom: 2px solid #000; padding: 2mm 1mm; font-weight: 900 !important; font-size: 9px !important; }
              .rc-table td { padding: 2mm 1mm; border-bottom: 1px dashed #000; font-size: 10px !important; }
              .rc-table .cc { width: 12%; text-align: center; }
              .rc-table .cd { width: 48%; }
              .rc-table .cp { width: 18%; text-align: right; }
              .rc-table .ct { width: 22%; text-align: right; font-weight: 700 !important; }
              .rc-total-s { border-top: 2px solid #000; padding-top: 3mm; margin-top: 2mm; }
              .rc-letras { font-size: 10px !important; font-weight: 700 !important; margin-bottom: 2mm; font-style: italic; }
              .rc-total { display: flex; justify-content: space-between; font-size: 14px !important; font-weight: 900 !important; padding: 2mm; border: 2px solid #000; margin-top: 2mm; }
              .rc-firmas { display: flex; justify-content: space-between; margin-top: 10mm; }
              .rc-firma { text-align: center; width: 45%; }
              .rc-firma-l { border-top: 1px solid #000; margin-bottom: 2mm; margin-top: 8mm; }
              .rc-firma-t { font-size: 9px !important; font-weight: 700 !important; }
              .rc-footer { text-align: center; font-size: 9px !important; margin-top: 4mm; padding-top: 3mm; border-top: 1px solid #000; }
              .rc-corte { text-align: center; margin-top: 5mm; padding-top: 3mm; border-top: 2px dashed #000; font-size: 10px !important; }
            }
          </style>
        </head>
        <body>
          <div class="rc">
            <div class="rc-header">
              <div class="rc-logo">${tiendaNombre}</div>
              <div class="rc-sub">DE TODO EN COMPUTACION</div>
              <div class="rc-sub">${tiendaDireccion}</div>
              <div class="rc-sub">Tel: ${tiendaTelefono}</div>
            </div>
            <div class="rc-tipo">RECIBO DE CAJA</div>
            <div class="rc-num">N° ${numero}</div>
            <div style="margin-bottom:3mm;">
              <div class="rc-row"><span class="rc-label">Fecha:</span><span>${formatDateLong(datos.fecha)}</span></div>
              <div class="rc-row"><span class="rc-label">Recibi de:</span><span>${datos.recibido_de}</span></div>
              ${datos.direccion ? `<div class="rc-row"><span class="rc-label">Direccion:</span><span>${datos.direccion}</span></div>` : ''}
              <div class="rc-row"><span class="rc-label">NIT:</span><span>${datos.nit || 'C/F'}</span></div>
            </div>
            <table class="rc-table">
              <thead><tr><th class="cc">CANT</th><th class="cd">DESCRIPCION</th><th class="cp">P/UNIT</th><th class="ct">TOTAL</th></tr></thead>
              <tbody>
                ${items.map(item => `<tr><td class="cc">${parseFloat(item.cantidad).toFixed(0)}</td><td class="cd">${item.descripcion}</td><td class="cp">${formatMoney(item.precio_unitario)}</td><td class="ct">${formatMoney(item.total)}</td></tr>`).join('')}
              </tbody>
            </table>
            <div class="rc-total-s">
              <div class="rc-letras">EN LETRAS: ${datos.total_letras}</div>
              <div class="rc-total"><span>TOTAL Q.</span><span>${formatMoney(total).replace('Q', '')}</span></div>
            </div>
            <div class="rc-firmas">
              <div class="rc-firma"><div class="rc-firma-l"></div><div class="rc-firma-t">${user?.nombre || ''}</div></div>
              <div class="rc-firma"><div class="rc-firma-l"></div><div class="rc-firma-t">FIRMA</div></div>
            </div>
            <div class="rc-footer"><p>Gracias por su preferencia</p></div>
            <div class="rc-corte">- - - - - - - - - - - - - - - - - - -</div>
          </div>
          <script>window.addEventListener('load', function() { setTimeout(function() { window.print(); }, 500); });</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const limpiarFormulario = () => {
    setForm({
      fecha: new Date().toISOString().split('T')[0],
      recibido_de: '',
      direccion: '',
      nit: 'C/F',
      items: [{ cantidad: 1, descripcion: '', precio_unitario: 0, total: 0 }],
      total_letras: '',
      metodo_pago: 'Efectivo',
      cuenta_bancaria_id: '',
      pos_id: '',
      comprobante: '',
      pagos_mixtos: [lineaVacia()]
    });
  };

  const handleEliminar = async (id, numero) => {
    if (!window.confirm(`¿Eliminar el recibo N° ${numero}?`)) return;
    try {
      await recibosApi.delete(id);
      loadRecibos();
      loadStats();
    } catch (err) {
      alert('Error al eliminar: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Recibos de Caja</h1>
          <p className="page-subtitle">
            {stats ? `${stats.total_recibos} recibos | ${formatMoney(stats.monto_mes)} este mes` : 'Gestion de recibos de caja'}
          </p>
        </div>
        <button onClick={() => { setShowForm(true); limpiarFormulario(); }} className="btn btn-primary">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="16"/>
            <line x1="8" y1="12" x2="16" y2="12"/>
          </svg>
          Nuevo Recibo
        </button>
      </div>

      {/* Estadísticas */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-color)' }}>{stats.recibos_hoy}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>Recibos Hoy</div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginTop: '4px' }}>{formatMoney(stats.monto_hoy)}</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#e65725' }}>{stats.recibos_mes}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>Recibos del Mes</div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginTop: '4px' }}>{formatMoney(stats.monto_mes)}</div>
          </div>
          <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#38a169' }}>{stats.total_recibos}</div>
            <div style={{ fontSize: '13px', color: 'var(--text-light)' }}>Total Recibos</div>
            <div style={{ fontSize: '14px', fontWeight: '600', marginTop: '4px' }}>{formatMoney(stats.total_monto)}</div>
          </div>
        </div>
      )}

      {/* Busqueda */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            type="text"
            className="form-input"
            placeholder="Buscar por numero o nombre..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleBuscar()}
            style={{ flex: 1 }}
          />
          <button onClick={handleBuscar} className="btn btn-primary">Buscar</button>
          {busqueda && (
            <button onClick={() => { setBusqueda(''); loadRecibos(); }} className="btn btn-secondary">Limpiar</button>
          )}
        </div>
      </div>

      {/* Listado de recibos */}
      <div className="card">
        {loading ? (
          <div className="loading"><div className="spinner"></div></div>
        ) : recibos.length === 0 ? (
          <div className="empty-state">
            <h3>No hay recibos</h3>
            <p>Cree un nuevo recibo de caja para empezar</p>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>N° Recibo</th>
                    <th>Fecha</th>
                    <th>Recibi de</th>
                    <th>NIT</th>
                    <th>Método</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                    <th>Creado por</th>
                    <th style={{ textAlign: 'center' }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {recibos.map(recibo => (
                    <tr key={recibo.id}>
                      <td>
                        <Link to={`/recibos/${recibo.id}`} style={{ fontWeight: '600', color: 'var(--primary-color)' }}>
                          {recibo.numero_recibo}
                        </Link>
                      </td>
                      <td>{formatDate(recibo.fecha)}</td>
                      <td style={{ fontWeight: '500' }}>{recibo.recibido_de}</td>
                      <td>{recibo.nit || 'C/F'}</td>
                      <td>{recibo.metodo_pago || 'Efectivo'}</td>
                      <td style={{ textAlign: 'right', fontWeight: '600' }}>{formatMoney(recibo.total)}</td>
                      <td style={{ fontSize: '13px', color: 'var(--text-light)' }}>{recibo.nombre_creador || '-'}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <Link to={`/recibos/${recibo.id}`} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>
                            Ver
                          </Link>
                          <button
                            onClick={() => handleEliminar(recibo.id, recibo.numero_recibo)}
                            className="btn btn-danger"
                            style={{ padding: '4px 10px', fontSize: '12px', background: '#e53e3e', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }}
                          >
                            Eliminar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            {pagination.pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
                {Array.from({ length: pagination.pages }, (_, i) => i + 1).map(page => (
                  <button
                    key={page}
                    onClick={() => loadRecibos(page)}
                    className={`btn ${page === pagination.page ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ padding: '6px 12px', fontSize: '13px' }}
                  >
                    {page}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal de nuevo recibo */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2>Nuevo Recibo de Caja</h2>
              <button className="modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Fecha</label>
                  <input type="date" className="form-input" value={form.fecha}
                    onChange={(e) => setForm(prev => ({ ...prev, fecha: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">NIT</label>
                  <input type="text" className="form-input" value={form.nit} placeholder="C/F"
                    onChange={(e) => setForm(prev => ({ ...prev, nit: e.target.value }))} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label className="form-label required">Recibi de</label>
                  <input type="text" className="form-input" placeholder="Nombre del cliente" value={form.recibido_de}
                    onChange={(e) => setForm(prev => ({ ...prev, recibido_de: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Direccion</label>
                  <input type="text" className="form-input" placeholder="Ciudad, direccion..." value={form.direccion}
                    onChange={(e) => setForm(prev => ({ ...prev, direccion: e.target.value }))} />
                </div>
              </div>

              {/* Items */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <h3 style={{ fontSize: '14px', fontWeight: '600', margin: 0 }}>Detalle</h3>
                  <button type="button" onClick={agregarItem} className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: '12px' }}>
                    + Linea
                  </button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid var(--border-color)', textAlign: 'left' }}>
                      <th style={{ padding: '6px', width: '70px', fontSize: '12px' }}>Cant.</th>
                      <th style={{ padding: '6px', fontSize: '12px' }}>Descripcion</th>
                      <th style={{ padding: '6px', width: '100px', fontSize: '12px' }}>P. Unit.</th>
                      <th style={{ padding: '6px', width: '100px', fontSize: '12px' }}>Total</th>
                      <th style={{ padding: '6px', width: '30px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.map((item, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '4px' }}>
                          <input type="number" min="1" className="form-input" style={{ padding: '6px', fontSize: '13px' }}
                            value={item.cantidad} onChange={(e) => handleItemChange(index, 'cantidad', e.target.value)} />
                        </td>
                        <td style={{ padding: '4px' }}>
                          <input type="text" className="form-input" style={{ padding: '6px', fontSize: '13px' }}
                            placeholder="Descripcion del producto o servicio"
                            value={item.descripcion} onChange={(e) => handleItemChange(index, 'descripcion', e.target.value)} />
                        </td>
                        <td style={{ padding: '4px' }}>
                          <input type="number" step="0.01" min="0" className="form-input"
                            style={{ padding: '6px', fontSize: '13px', textAlign: 'right' }}
                            value={item.precio_unitario} onChange={(e) => handleItemChange(index, 'precio_unitario', e.target.value)} />
                        </td>
                        <td style={{ padding: '4px', textAlign: 'right', fontWeight: '600', fontSize: '13px' }}>
                          {formatMoney(item.total)}
                        </td>
                        <td style={{ padding: '4px', textAlign: 'center' }}>
                          {form.items.length > 1 && (
                            <button type="button" onClick={() => eliminarItem(index)}
                              style={{ background: 'none', border: 'none', color: 'var(--danger-color)', cursor: 'pointer', fontSize: '16px' }}>
                              &times;
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Método de Pago */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Método de Pago</label>
                  <select className="form-input" value={form.metodo_pago}
                    onChange={(e) => setForm(prev => ({ ...prev, metodo_pago: e.target.value, cuenta_bancaria_id: '', pos_id: '' }))}>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Transferencia">Transferencia</option>
                    <option value="POS">POS</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Mixto">Pago Mixto</option>
                  </select>
                </div>
                {form.metodo_pago === 'Transferencia' && (
                  <div className="form-group">
                    <label className="form-label required">Cuenta Bancaria</label>
                    <select className="form-input" value={form.cuenta_bancaria_id}
                      onChange={(e) => setForm(prev => ({ ...prev, cuenta_bancaria_id: e.target.value }))}>
                      <option value="">-- Seleccionar --</option>
                      {cuentas.map(c => <option key={c.id} value={c.id}>{c.nombre} - {c.banco}</option>)}
                    </select>
                  </div>
                )}
                {form.metodo_pago === 'POS' && (
                  <div className="form-group">
                    <label className="form-label required">Terminal POS</label>
                    <select className="form-input" value={form.pos_id}
                      onChange={(e) => setForm(prev => ({ ...prev, pos_id: e.target.value }))}>
                      <option value="">-- Seleccionar --</option>
                      {posTerminales.map(p => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </div>
                )}
                {form.metodo_pago === 'Cheque' && (
                  <div className="form-group">
                    <label className="form-label">No. Cheque / Referencia</label>
                    <input type="text" className="form-input" value={form.comprobante}
                      onChange={(e) => setForm(prev => ({ ...prev, comprobante: e.target.value }))} />
                  </div>
                )}
              </div>

              {form.metodo_pago === 'Mixto' && (
                <div style={{ marginBottom: '16px' }}>
                  <label className="form-label">Desglose del Pago Mixto</label>
                  <PagoMixtoForm
                    pagos={form.pagos_mixtos}
                    onChange={(pagos) => setForm(prev => ({ ...prev, pagos_mixtos: pagos }))}
                    total={calcularTotal(form.items)}
                    cuentas={cuentas}
                    posTerminales={posTerminales}
                  />
                </div>
              )}

              {/* Totales */}
              <div style={{ background: '#f8fafc', padding: '16px', borderRadius: '8px' }}>
                <div style={{ marginBottom: '8px' }}>
                  <label className="form-label" style={{ fontSize: '12px' }}>Total en Letras</label>
                  <input type="text" className="form-input" value={form.total_letras}
                    onChange={(e) => setForm(prev => ({ ...prev, total_letras: e.target.value }))}
                    placeholder="Se genera automaticamente" style={{ fontStyle: 'italic', fontSize: '13px' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>TOTAL:</span>
                  <span style={{ fontSize: '22px', fontWeight: '700', color: 'var(--primary-color)' }}>
                    {formatMoney(calcularTotal(form.items))}
                  </span>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleGuardar} disabled={guardando}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
                  <polyline points="17 21 17 13 7 13 7 21"/>
                  <polyline points="7 3 7 8 15 8"/>
                </svg>
                {guardando ? 'Guardando...' : 'Guardar e Imprimir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recibos;
